/**
 * Country-based personal-income tax estimates — v1 (launch).
 *
 * Design principles:
 * - Isolated from the rest of the app. Every consumer must call
 *   `estimateTax(country, income)` and never inline percentages.
 * - v1 supports a small set of countries with either a flat headline
 *   rate OR a very simple bands ladder where we already knew them.
 * - Countries not listed here fall back to a "generic estimate"
 *   that we clearly label as such.
 * - Future extensions (VAT/GST, self-employed, company, regional,
 *   student exemptions, tax bands, etc.) plug into `computeFor`
 *   without touching call sites.
 *
 * NB: This is a personal-finance ESTIMATE ONLY — never used for
 * invoices, subscriptions or Stripe billing. All headline rates are
 * simplifications and should be updated as legislation changes.
 */

/**
 * @typedef {Object} TaxCountry
 * @property {string} code   — ISO 3166-1 alpha-2
 * @property {string} name   — English display name
 * @property {string} currency — ISO 4217 (for display only)
 * @property {"bands"|"flat"|"none"} model
 * @property {number} [flat_rate]  — 0..1, used when model === "flat"
 * @property {Array<{upto: number|null, rate: number, allowance?: number}>} [bands]
 * @property {number} [personal_allowance]
 * @property {string} [note]
 */

/** @type {TaxCountry[]} — baked-in fallback used until server rules arrive
 *  or when the network fails. Kept in sync with `backend/core/tax_seed.py`. */
export const TAX_COUNTRIES_FALLBACK = [
    {
        code: "GB", name: "United Kingdom", currency: "GBP", model: "bands",
        personal_allowance: 12570,
        bands: [
            { upto: 37700, rate: 0.20 },
            { upto: 125140, rate: 0.40 },
            { upto: null, rate: 0.45 },
        ],
        note: "England, Scotland and Wales have different bands — this uses the England/Wales figures.",
    },
    {
        code: "US", name: "United States", currency: "USD", model: "bands",
        personal_allowance: 14600,
        bands: [
            { upto: 11600, rate: 0.10 },
            { upto: 47150, rate: 0.12 },
            { upto: 100525, rate: 0.22 },
            { upto: 191950, rate: 0.24 },
            { upto: 243725, rate: 0.32 },
            { upto: 609350, rate: 0.35 },
            { upto: null, rate: 0.37 },
        ],
        note: "Federal income tax for single filers. State taxes and FICA are not included.",
    },
    {
        code: "CA", name: "Canada", currency: "CAD", model: "bands",
        personal_allowance: 15705,
        bands: [
            { upto: 55867, rate: 0.15 },
            { upto: 111733, rate: 0.205 },
            { upto: 173205, rate: 0.26 },
            { upto: 246752, rate: 0.29 },
            { upto: null, rate: 0.33 },
        ],
        note: "Federal only. Provincial taxes vary.",
    },
    {
        code: "AU", name: "Australia", currency: "AUD", model: "bands",
        personal_allowance: 18200,
        bands: [
            { upto: 45000, rate: 0.19 },
            { upto: 135000, rate: 0.325 },
            { upto: 190000, rate: 0.37 },
            { upto: null, rate: 0.45 },
        ],
        note: "Resident individual rates. Medicare Levy not included.",
    },
    {
        code: "DE", name: "Germany", currency: "EUR", model: "flat",
        flat_rate: 0.30,
        note: "Simplified average — Germany's progressive scale ranges 0 % → 45 %.",
    },
    {
        code: "FR", name: "France", currency: "EUR", model: "flat",
        flat_rate: 0.30,
        note: "Simplified average — France's progressive scale ranges 0 % → 45 %.",
    },
    {
        code: "ES", name: "Spain", currency: "EUR", model: "flat",
        flat_rate: 0.24,
        note: "Simplified national estimate — regional (autonómica) rates vary.",
    },
    {
        code: "IT", name: "Italy", currency: "EUR", model: "flat",
        flat_rate: 0.29,
        note: "Simplified — IRPEF progressive rates 23 % → 43 % plus regional surcharges.",
    },
    {
        code: "NL", name: "Netherlands", currency: "EUR", model: "flat",
        flat_rate: 0.37,
        note: "Simplified — 2-box progressive system with box 1 at 36.97 % and 49.5 %.",
    },
    {
        code: "IE", name: "Ireland", currency: "EUR", model: "flat",
        flat_rate: 0.30,
        note: "Simplified — 20 %/40 % bands, plus PRSI and USC.",
    },
    {
        code: "PT", name: "Portugal", currency: "EUR", model: "flat",
        flat_rate: 0.28,
        note: "Simplified — progressive scale 14.5 % → 48 %.",
    },
    {
        code: "NZ", name: "New Zealand", currency: "NZD", model: "flat",
        flat_rate: 0.24,
        note: "Simplified — progressive scale 10.5 % → 39 %.",
    },
    {
        code: "IN", name: "India", currency: "INR", model: "flat",
        flat_rate: 0.20,
        note: "Simplified — new-regime slabs 5 % → 30 %.",
    },
    {
        code: "SG", name: "Singapore", currency: "SGD", model: "flat",
        flat_rate: 0.15,
        note: "Simplified — progressive scale 0 % → 24 %.",
    },
    {
        code: "AE", name: "United Arab Emirates", currency: "AED", model: "none",
        note: "No personal income tax on residents.",
    },
    {
        code: "ZA", name: "South Africa", currency: "ZAR", model: "flat",
        flat_rate: 0.26,
        note: "Simplified — progressive scale 18 % → 45 %.",
    },
    {
        code: "BR", name: "Brazil", currency: "BRL", model: "flat",
        flat_rate: 0.22,
        note: "Simplified — progressive scale 0 % → 27.5 %.",
    },
    {
        code: "MX", name: "Mexico", currency: "MXN", model: "flat",
        flat_rate: 0.25,
        note: "Simplified — progressive scale 1.92 % → 35 %.",
    },
    {
        code: "JP", name: "Japan", currency: "JPY", model: "flat",
        flat_rate: 0.23,
        note: "Simplified — progressive national scale 5 % → 45 %.",
    },
    {
        code: "KR", name: "South Korea", currency: "KRW", model: "flat",
        flat_rate: 0.22,
        note: "Simplified — progressive scale 6 % → 45 %.",
    },
    {
        code: "SE", name: "Sweden", currency: "SEK", model: "flat",
        flat_rate: 0.32,
        note: "Simplified — municipal (avg 32 %) + national surcharge above threshold.",
    },
    {
        code: "NO", name: "Norway", currency: "NOK", model: "flat",
        flat_rate: 0.28,
        note: "Simplified — flat plus bracketed step-tax and social contributions.",
    },
    {
        code: "CH", name: "Switzerland", currency: "CHF", model: "flat",
        flat_rate: 0.20,
        note: "Simplified — federal + cantonal + communal vary widely.",
    },
];

const _BY_CODE = Object.fromEntries(TAX_COUNTRIES_FALLBACK.map((c) => [c.code, c]));

// --- Server-hydrated rules ------------------------------------------
//
// The frontend always renders with a baked-in fallback so users on
// slow / offline networks still see a sensible estimate immediately.
// On boot we hit `/api/config/tax/countries` — a public endpoint that
// reads the current rules from Mongo — and swap the in-memory table so
// consumers pick up rate changes without a redeploy.

import { api } from "./api";

const CACHE_KEY = "vu_tax_rules_v1";

/** Current in-memory rules table. Mutated by `loadTaxRulesFromServer()`
 *  and by `useTaxCountries()`. Never reassigned to null. */
let _currentCountries = TAX_COUNTRIES_FALLBACK;
let _currentByCode = _BY_CODE;
let _currentVersion = "fallback";
const _listeners = new Set();

// Hydrate synchronously from localStorage cache (if present) so a
// returning user gets the latest rules on the very first render — no
// flash of stale-then-fresh in the UI.
try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && Array.isArray(parsed.countries) && parsed.countries.length) {
            _currentCountries = parsed.countries;
            _currentByCode = Object.fromEntries(parsed.countries.map((c) => [c.code, c]));
            _currentVersion = parsed.version || "cache";
        }
    }
} catch { /* private-mode / quota — non-fatal */ }

let _loadPromise = null;

/** Fetch the server-side rules table and swap it in. Idempotent —
 *  multiple concurrent callers share one in-flight request. */
export function loadTaxRulesFromServer() {
    if (_loadPromise) return _loadPromise;
    _loadPromise = (async () => {
        try {
            const { data } = await api.get("/config/tax/countries");
            if (data && Array.isArray(data.countries) && data.countries.length) {
                _currentCountries = data.countries;
                _currentByCode = Object.fromEntries(data.countries.map((c) => [c.code, c]));
                _currentVersion = data.version || "server";
                try {
                    localStorage.setItem(CACHE_KEY, JSON.stringify({
                        version: _currentVersion,
                        countries: _currentCountries,
                    }));
                } catch { /* ignore quota errors */ }
                _listeners.forEach((fn) => { try { fn(); } catch { /* noop */ } });
            }
        } catch {
            // Silent: fallback rules keep the app usable offline.
        }
        return _currentCountries;
    })();
    return _loadPromise;
}

/** Subscribe to rule-table updates. Returns an unsubscribe function. */
export function subscribeTaxRules(fn) {
    _listeners.add(fn);
    return () => _listeners.delete(fn);
}

// Kick off the fetch at module load — non-blocking. Consumers get the
// fallback on first render and re-render via `useTaxCountries()` when
// the server table arrives.
loadTaxRulesFromServer();

export function findTaxCountry(code) {
    if (!code) return null;
    return _currentByCode[code.toUpperCase()] || null;
}

/**
 * Compute a v1 tax estimate for the given income. All values are in
 * the country's native currency; conversion is the caller's job.
 *
 * @param {string|null} countryCode  ISO alpha-2, or null → generic fallback
 * @param {number} annualIncome
 * @returns {{
 *   supported: boolean,
 *   country: TaxCountry|null,
 *   tax: number,
 *   effective_rate: number,
 *   breakdown: Array<{label: string, amount: number, rate?: number}>,
 *   personal_allowance: number,
 *   taxable: number,
 *   note?: string,
 * }}
 */
export function estimateTax(countryCode, annualIncome) {
    const income = Number(annualIncome) || 0;
    const country = findTaxCountry(countryCode);

    // Unsupported / unknown → generic 20 % fallback with a clear label.
    if (!country) {
        const tax = income * 0.20;
        return {
            supported: false,
            country: null,
            tax,
            effective_rate: income > 0 ? tax / income : 0,
            breakdown: [{ label: "Generic 20% estimate", amount: tax, rate: 0.20 }],
            personal_allowance: 0,
            taxable: income,
            note: "Your country isn't in our v1 list — this is a 20 % placeholder estimate.",
        };
    }

    if (country.model === "none") {
        return {
            supported: true, country, tax: 0, effective_rate: 0,
            breakdown: [{ label: "No personal income tax", amount: 0 }],
            personal_allowance: 0, taxable: income, note: country.note,
        };
    }

    if (country.model === "flat") {
        const rate = country.flat_rate ?? 0;
        const tax = Math.max(0, income) * rate;
        return {
            supported: true, country, tax,
            effective_rate: income > 0 ? tax / income : 0,
            breakdown: [{ label: `Flat estimate (${(rate * 100).toFixed(1)}%)`, amount: tax, rate }],
            personal_allowance: 0, taxable: income, note: country.note,
        };
    }

    // Bands ladder.
    const personal = country.personal_allowance || 0;
    let remaining = Math.max(0, income - personal);
    const taxable = remaining;
    const breakdown = [];
    let carried = 0; // amount of `remaining` already consumed by lower bands
    for (const b of country.bands || []) {
        const bandCap = b.upto == null ? Infinity : (b.upto - carried);
        const inThisBand = Math.min(remaining, Math.max(0, bandCap));
        const tax = inThisBand * b.rate;
        if (inThisBand > 0) {
            breakdown.push({
                label: `${(b.rate * 100).toFixed(0)}% band`,
                amount: tax, rate: b.rate,
            });
        }
        remaining -= inThisBand;
        carried += inThisBand;
        if (remaining <= 0) break;
    }
    const tax = breakdown.reduce((s, b) => s + b.amount, 0);
    return {
        supported: true, country, tax,
        effective_rate: income > 0 ? tax / income : 0,
        breakdown, personal_allowance: personal, taxable, note: country.note,
    };
}

/** UI helper — returns a de-duplicated, alphabetised list for select boxes. */
export function taxCountriesSorted() {
    return [..._currentCountries].sort((a, b) => a.name.localeCompare(b.name));
}

/** Current rules version — surfaced in the UI so power-users know when
 *  the table has been refreshed. */
export function currentTaxRulesVersion() {
    return _currentVersion;
}
